import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WebSocket hook for LIMITS execution-layer updates
 * 
 * Provides real-time:
 * - Price ticks (4/sec)
 * - Position P&L updates
 * - Trailing stop ratchet notifications
 * - Trade close events (SL/TP hit)
 * - Structure redraw events (candle-close based only)
 * 
 * NOTE: Structure redraw events are triggered on candle close only.
 * This maintains LIMITS framework integrity.
 */
export const useExecutionWebSocket = (onPriceTick, onPositionUpdate, onTradeClosed, onStructureRedraw, symbols = ["BTC/USD"]) => {
  const [connected, setConnected] = useState(false);
  const [lastPrice, setLastPrice] = useState(null);
  const [subscribedSymbols, setSubscribedSymbols] = useState(symbols);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    // Convert HTTP URL to WebSocket URL - use /api prefix for ingress routing
    const symbolsParam = encodeURIComponent(symbols.join(","));
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.host;
const wsUrl = `${protocol}://${host}/api/ws?symbols=${symbolsParam}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setConnected(true);
        reconnectAttempts.current = 0;
        console.log('[WS] Execution WebSocket connected, symbols:', symbols);
      };
      
      wsRef.current.onclose = () => {
        setConnected(false);
        console.log('[WS] Execution WebSocket disconnected');
        
        // Auto-reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('[WS] Execution WebSocket error:', error);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'price_tick':
              setLastPrice(data.price);
              if (onPriceTick) onPriceTick(data);
              break;
              
            case 'execution_update':
              // Process position updates and trade closes
              if (data.updates) {
                data.updates.forEach(update => {
                  if (update.type === 'position_update' && onPositionUpdate) {
                    onPositionUpdate(update);
                  } else if (update.type === 'trade_closed' && onTradeClosed) {
                    onTradeClosed(update);
                  }
                });
              }
              break;
              
            case 'structure_redraw':
              // LIMITS structure redraw (candle-close based)
              console.log('[WS] Structure redraw:', data.timeframe, data.prior_direction, '→', data.new_direction);
              if (onStructureRedraw) onStructureRedraw(data);
              break;
              
            case 'subscribed':
            case 'unsubscribed':
              setSubscribedSymbols(data.subscribed_symbols || []);
              break;
              
            case 'pong':
              // Heartbeat response
              if (data.subscribed_symbols) {
                setSubscribedSymbols(data.subscribed_symbols);
              }
              break;
              
            default:
              break;
          }
        } catch (e) {
          console.error('[WS] Failed to parse message:', e);
        }
      };
    } catch (e) {
      console.error('[WS] Failed to connect:', e);
    }
  }, [onPriceTick, onPositionUpdate, onTradeClosed, onStructureRedraw, symbols]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  useEffect(() => {
    // TEMP: disable WS while restoring chart stability
    return;
    // TEMP STABILIZATION: disable execution WS reconnect storm
    return;
    connect();
    
    // Heartbeat every 30 seconds
    const heartbeat = setInterval(sendPing, 30000);
    
    return () => {
      clearInterval(heartbeat);
      disconnect();
    };
  }, [connect, disconnect, sendPing]);

  // Subscribe/unsubscribe to symbols
  const subscribe = useCallback((symbol) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(`subscribe:${symbol}`);
    }
  }, []);

  const unsubscribe = useCallback((symbol) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(`unsubscribe:${symbol}`);
    }
  }, []);

  return {
    connected,
    lastPrice,
    subscribedSymbols,
    reconnect: connect,
    disconnect,
    subscribe,
    unsubscribe
  };
};

export default useExecutionWebSocket;
